import { useCallback, useEffect, useState } from 'react';
import dayjs from 'dayjs';

import type { TimeId } from 'Stores/BidCountSortStore/BidCountSortStore';
import { useTimeId } from 'Stores/BidCountSortStore/BidCountSortStore';
import { TIME_IN_MS } from 'Utilities/consts';
import useSortOrder from 'Utilities/hooks/vehicleList/useSortOrder/useSortOrder';

import { VEHICLE_SORT_CONDITIONAL_OPTIONS } from './useSortOrder/useSortOrder.const';

export const BID_COUNTS_STALE_INTERVAL = 5;

const getTimeRemainingForNewSort = (timeId: TimeId) => {
	return dayjs(timeId).add(BID_COUNTS_STALE_INTERVAL, 'minute').diff(dayjs());
};

export const useIsBidCountsStale = (): boolean => {
	const timeId = useTimeId();
	const { sort } = useSortOrder();
	const [isPromptVisible, setIsPromptVisible] = useState(false);

	const isBidCountSort = sort === VEHICLE_SORT_CONDITIONAL_OPTIONS.bidCounts;

	const hasTimeIdExpired = useCallback(() => {
		return getTimeRemainingForNewSort(timeId) <= 0;
	}, [timeId]);

	const handlePromptVisibility = useCallback(() => {
		if (isBidCountSort && hasTimeIdExpired()) {
			setIsPromptVisible(true);
		}
	}, [isBidCountSort, hasTimeIdExpired]);

	useEffect(() => {
		const isPromptVisibleAndHasTimeIdExpired = isPromptVisible && !timeId;
		if (isPromptVisibleAndHasTimeIdExpired) {
			setIsPromptVisible(false);
			return;
		}

		const isUsingPromptOnNonBidCountSort = !timeId || !isBidCountSort;
		if (isUsingPromptOnNonBidCountSort) {
			return;
		}

		const differenceWithBuffer = getTimeRemainingForNewSort(timeId) + TIME_IN_MS.TEN_SECONDS;
		const timeoutId = setTimeout(() => {
			handlePromptVisibility();
		}, differenceWithBuffer);

		handlePromptVisibility();

		// eslint-disable-next-line consistent-return
		return () => clearTimeout(timeoutId);
	}, [handlePromptVisibility, timeId, isBidCountSort, isPromptVisible]);

	return isBidCountSort && isPromptVisible;
};
